<template lang="pug">
UpsertPage(
  :title="title"
  :disabled="disabled"
  :showAuditTrail="isEditProduct && canViewAuditTrail"
  objectType="Product",
  :objectKey="productKey"
  :canSave="canSave"
  :pending="isSaving"
  @save="validateTranslationsAndSave"
  @cancel="$emit('cancel')"
  )
  // todo: consider extracting multilanguage
  BriqMultilanguageWarningModal(
    v-if="useMultilanguage"
    v-show="showTranslationWarningModal"
    :defaultModel="product"
    :translationInfo="product.TranslationInfo"
    :translatableProperties="translatableProperties"
    @close="showTranslationWarningModal = false" 
    @onSubmit="save"
    )
  BriqMultilanguagePickerPanel(
    v-if="useMultilanguage"
    v-model="activeCulture"
    :defaultCulture="defaultCulture.DisplayName"
    :cultures="availableCultures"
    :showSecondColumn="shouldShowMultilanguageColumn"
    @toggle="toggleShowSecondColumn"
    )
  ResourceField(
    :disabled="disabled"
    :value="product.ResourceKey"
    :resources="forEditorModel.Resources"
    :showSecondColumn="shouldShowMultilanguageColumn"
    :resourceKeyError="resourceKeyError"
    @resourceSelected="resourceSelected"
    )
  Prefill(
    v-if="canAddExternalProducts"
    :canAddMultipleExternalItems="canAddMultipleExternalItems"
    feedType="product"
    :error="prefillError"
    :options="productFeedOptions"
    :selectedExternalIds="product.ExternalProductIds"
    :display="displayProduct"
    :formatExternalItem="formatProduct"
    :disabled="disabled"
    @selected="selectExternalProduct"
    @decouple="decoupleExternalProduct"
    )
  ProductFields(
    v-model="product"
    :disabled="disabled"
    :forEditorModel="forEditorModel"
    :activeCulture="activeCulture"
    :show-accounting-fields="showAccountingFields"
    :enableProductDurationPerParticipant="enableProductDurationPerParticipant"
    :showSecondColumn="shouldShowMultilanguageColumn"
    :vuelidate="vuelidate"
    :selectedResource="selectedResource"
    @schedulingBehaviorSelected="x => $emit('schedulingBehaviorSelected', x)"
    )
  DealsTable(v-if="isEditProduct && deals" :deals="deals" :priceFormatter="priceFormatter")
</template>

<script>
import Vue from 'vue'
import { currencyFormatter } from '@/helpers/intlFormatter.js'
import UpsertPage from '@v/components/UpsertPage.vue'
import Prefill from '@v/components/Prefill.vue'
import ProductFields from '@v/inventory/products/edit/ProductFields.vue'
import ResourceField from './ResourceField.vue'
import DealsTable from '@/vue/inventory/products/edit/DealsTable.vue'
import { displayProduct, formatProduct } from '@v/components/prefillMatchFormatter.js'
import {
  getExternalDeletedItems,
  deletedExternalItemErrorFormatter,
} from '@v/util/deletedExternalItemsHelper.js'
import BriqMultilanguagePickerPanel from '@v/components/BriqMultilanguagePickerPanel.vue'
import BriqMultilanguageWarningModal from '@/vue/components/BriqMultilanguageWarningModal.vue'
import { emptyTranslatableFieldsExist } from '@v/util/emptyTranslatableFields.js'
import {
  find,
  propEq,
  pathEq,
  hasPath,
  not,
  compose,
  allPass,
  without,
  pipe,
  prop,
  assoc,
  reduce,
  concat,
  chain,
  propOr,
} from 'ramda'

export default {
  name: 'UpsertProduct',
  components: {
    UpsertPage,
    Prefill,
    ProductFields,
    DealsTable,
    ResourceField,
    BriqMultilanguagePickerPanel,
    BriqMultilanguageWarningModal,
  },
  props: {
    forEditorModel: { type: Object, required: true },
    product: Object,
    disabled: Boolean,
    isSaving: Boolean,
    priceIncludesTaxDefaultValue: Boolean, // for create
    enableProductDurationPerParticipant: Boolean,
    showAccountingFields: Boolean,
    canViewAuditTrail: Boolean,
    vuelidate: { type: Object, required: true },
    enableMultilanguage: Boolean, // feature flag
  },
  data() {
    return {
      isMounted: false,
      showSecondColumn: false,
      activeCulture: null,
      displayProduct,
      formatProduct,
      showTranslationWarningModal: false,
      deletedExternalProductErrorMessage: null, // if external products have been deleted from external system, it should show it for the coupled briq products
    }
  },
  computed: {
    productKey() {
      return this.product?.Key
    },
    isEditProduct() {
      return !!this.productKey
    },
    title() {
      return this.isEditProduct ? 'Edit product' : 'Create product'
    },
    canSave() {
      return !this.disabled && this.isMounted
    },
    selectedResource() {
      return find(propEq('Key', this.product.ResourceKey))(this.resources)
    },
    canAddExternalProducts() {
      if (!this.selectedResource) return false
      const featurePath = [this.selectedResource.ExternalType, 'ResourceLinkingToExternalResource']
      return allPass([hasPath(featurePath), compose(not, pathEq(featurePath, 'NotAllowed'))])(
        this.forEditorModel.FeatureFlags,
      )
    },
    resources() {
      return this.forEditorModel?.Resources
    },
    resourceKeyError() {
      if (this.vuelidate.product?.ResourceKey?.$error) return 'Please choose a resource'
      return null
    },
    defaultCulture() {
      return this.product.TranslationInfo?.DefaultCulture
    },
    availableCultures() {
      return this.product.TranslationInfo?.AvailableCultures?.filter(
        c => c.Name !== this.defaultCulture.Name,
      )
    },
    canAddMultipleExternalItems() {
      if (!this.selectedResource) return false
      const featurePath = [this.selectedResource.ExternalType, 'ResourceLinkingToExternalResource']
      return allPass([hasPath(featurePath), pathEq(featurePath, 'Multiple')])(
        this.forEditorModel.FeatureFlags,
      )
    },
    productFeedOptions() {
      const makeExternalId = pipe(prop('ExternalProductIds'), propOr(null, 0))
      return this.forEditorModel.ProductFeeds.map(chain(assoc('ExternalId'), makeExternalId))
    },
    priceFormatter() {
      return currencyFormatter(this.$locale, this.$currency)
    },
    deals() {
      return this.forEditorModel.RelatedProductGroups
    },
    useMultilanguage() {
      return (
        this.isEditProduct &&
        this.enableMultilanguage &&
        !!this.product.TranslationInfo?.AvailableCultures?.length
      )
    },
    shouldShowMultilanguageColumn() {
      return this.useMultilanguage && this.showSecondColumn
    },
    translatableProperties() {
      return [
        {
          label: 'Public name',
          key: 'NameExternal',
        },
        {
          label: 'Description',
          key: 'Description',
        },
      ]
    },
    prefillError() {
      if (!this.isMounted) return null
      if (this.vuelidate.product.ExternalProductIds.$error) return 'Please link an external product'
      return this.deletedExternalProductErrorMessage
    },
    selectedExternalProduct() {
      return { ExternalProductIds: this.product?.ExternalProductIds }
    },
  },
  methods: {
    selectExternalProduct(suggestion) {
      const externalIdsIsArray = Array.isArray(this.product.ExternalProductIds)
      const externalProductsData = externalIdsIsArray ? this.product.ExternalProductIds : []
      externalProductsData.push(suggestion.item.ExternalProductIds[0])
      const externalProperties = {}
      for (let [key, value] of Object.entries(suggestion.item)) {
        // Only properties with a value other than `null` and key different than ResourceKey should be overwritten
        if (value !== null && key !== 'ResourceKey') externalProperties[key] = value
      }
      if (externalProductsData.length === 1) {
        for (const [key, value] of Object.entries(externalProperties)) {
          if (this.product[key] !== undefined) this.product[key] = value
        }
      }
      Vue.set(this.product, 'ExternalProductIds', externalProductsData)
    },
    resourceSelected(value) {
      this.product.ResourceKey = value
      this.product.ExternalProductIds = []
      this.$bus.$emit('resourceSelected', value)
    },
    toggleShowSecondColumn() {
      this.showSecondColumn = !this.showSecondColumn
    },
    decoupleExternalProduct(externalProductId) {
      this.product.ExternalProductIds = pipe(
        prop('ExternalProductIds'),
        without([externalProductId]),
      )(this.product)
    },
    validateTranslationsAndSave() {
      const translationsAreMissing =
        this.useMultilanguage &&
        emptyTranslatableFieldsExist(
          this.product,
          this.product.TranslationInfo,
          this.translatableProperties,
        )
      if (translationsAreMissing) this.showTranslationWarningModal = true
      else this.save()
    },
    save() {
      const deletedExternalProducts = getExternalDeletedItems({
        externalItemsIds: this.product.ExternalProductIds,
        feedOptions: this.forEditorModel.ProductFeeds,
        itemId: 'ExternalProductIds',
      })
      if (deletedExternalProducts?.length > 0) {
        this.$notifyError(`Please remove previously deleted external products (${deletedExternalProducts.join(', ')}) before saving this product.`)
        return
      }
      this.$emit('save', this.product)
    },
  },
  beforeMount() {
    if (this.useMultilanguage) {
      this.activeCulture = this.availableCultures[0]
    }

    const deletedExternalProducts = getExternalDeletedItems({
      externalItemsIds: this.product.ExternalProductIds,
      feedOptions: this.forEditorModel.ProductFeeds,
      itemId: 'ExternalProductIds',
    })
    this.deletedExternalProductErrorMessage = deletedExternalItemErrorFormatter({
      deletedExternalItems: deletedExternalProducts,
      source: 'products',
    })
  },
  mounted() {
    this.isMounted = true
  },
}
</script>
